import { FunctionsHttpError } from '@supabase/supabase-js';
import { z } from 'zod';
import { supabase } from '@/lib/supabase';
import { ApiError, toApiError } from './errors';

export const assistantReplySchema = z.object({
  reply: z.string(),
  mode: z.enum(['tools', 'json', 'fallback']),
  degraded: z.boolean(),
  tools: z.array(z.string()),
  actions: z.array(z.object({ type: z.literal('book'), serviceId: z.string(), startsAt: z.string().optional() })).default([]),
});
export type AssistantReply = z.infer<typeof assistantReplySchema>;

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

const ERRORS: Record<string, string> = {
  rate_limited: 'Слишком много вопросов подряд. Попробуйте через несколько минут.',
  tenant_budget: 'Помощник на сегодня исчерпал лимит. Запишитесь обычным способом или позвоните.',
  global_budget: 'Помощник временно недоступен. Запишитесь обычным способом или позвоните.',
  disabled: 'Помощник отключён в этой студии.',
  not_configured: 'Помощник пока не настроен.',
  forbidden: 'Нет доступа.',
};

/** Calls the `assistant` Edge Function. The LLM key never leaves the server. */
export async function askAssistant(input: {
  slug: string;
  scope: 'client' | 'owner';
  messages: ChatTurn[];
  bookingToken?: string | null;
}): Promise<AssistantReply> {
  const { data, error } = await supabase.functions.invoke('assistant', {
    body: { slug: input.slug, scope: input.scope, messages: input.messages.slice(-12), bookingToken: input.bookingToken ?? null },
  });
  if (error) {
    if (error instanceof FunctionsHttpError) {
      const body = (await error.context.json().catch(() => ({}))) as { error?: string };
      const code = body.error ?? 'unknown';
      throw new ApiError(code, ERRORS[code] ?? 'Помощник сейчас недоступен. Записаться можно обычным способом.');
    }
    throw toApiError(error);
  }
  const parsed = assistantReplySchema.safeParse(data);
  if (!parsed.success) throw new ApiError('unknown', 'Помощник ответил непонятно. Попробуйте ещё раз.');
  return parsed.data;
}
